import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { StatCard } from '../../components/ui/StatCard';
import { Calendar, MapPin, Users, Recycle, ArrowRight, Download, Share2, CheckCircle2, FileText, Video, Image as ImageIcon, Clock } from 'lucide-react';
export function SeriesDetailPage() {
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'tentang' | 'konten' | 'event' | 'galeri'>('tentang');
  // Mock Data based on ID (simplified for demo)
  const seriesData = {
    title: 'HPSN 2024: Atasi Sampah Plastik dengan Cara Produktif',
    description: 'Hari Peduli Sampah Nasional 2024 menjadi momentum penting untuk memperkuat komitmen pengelolaan sampah berkelanjutan di Indonesia.',
    status: 'active',
    stats: {
      konten: 24,
      event: 156,
      partisipan: '45.2K',
      tonase: '1.2K'
    },
    timeline: [{
      date: '21 Feb',
      title: 'Puncak Peringatan HPSN',
      status: 'completed'
    }, {
      date: '25 Feb',
      title: 'Aksi Bersih Serentak',
      status: 'completed'
    }, {
      date: '01 Mar',
      title: 'Webinar Nasional',
      status: 'upcoming'
    }, {
      date: '15 Mar',
      title: 'Penutupan Rangkaian',
      status: 'upcoming'
    }]
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-4901">
      <Header data-id="element-4902" />

      <main className="flex-1" data-id="element-4903">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-700 to-secondary-800 text-white py-16 relative overflow-hidden" data-id="element-4904">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-4905"></div>
          <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-4906">
            <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-4907">
              <div className="flex-1" data-id="element-4908">
                <div className="flex items-center gap-3 mb-4" data-id="element-4909">
                  <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm" data-id="element-4910">
                    Series Tematik Nasional
                  </Badge>
                  <Badge className="bg-green-500 text-white border-none" data-id="element-4911">
                    Sedang Berlangsung
                  </Badge>
                </div>
                <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-4912">
                  {seriesData.title}
                </h1>
                <p className="text-lg text-white/90 mb-8 max-w-3xl leading-relaxed" data-id="element-4913">
                  {seriesData.description}
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-4914">
                  <Button size="lg" className="bg-white text-primary-700 hover:bg-gray-100 font-semibold" data-id="element-4915">
                    Ikut Berkontribusi
                  </Button>
                  <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10" data-id="element-4916">
                    <Share2 className="mr-2 h-4 w-4" data-id="element-4917" /> Bagikan
                  </Button>
                </div>
              </div>

              {/* Progress Card */}
              <Card className="w-full md:w-80 bg-white/10 backdrop-blur-md border-white/20 text-white p-6" data-id="element-4918">
                <h3 className="font-semibold mb-4 flex items-center gap-2" data-id="element-4919">
                  <Clock className="h-4 w-4" data-id="element-4920" /> Timeline Series
                </h3>
                <div className="space-y-6 relative" data-id="element-4921">
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-white/20" data-id="element-4922"></div>
                  {seriesData.timeline.map((item, idx) => <div key={idx} className="relative pl-6" data-id="element-4923">
                      <div className={`absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-white ${item.status === 'completed' ? 'bg-green-500' : 'bg-transparent'}`} data-id="element-4924"></div>
                      <p className="text-xs opacity-70" data-id="element-4925">{item.date}</p>
                      <p className="font-medium text-sm" data-id="element-4926">{item.title}</p>
                    </div>)}
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-8 -mt-8 relative z-20 container mx-auto px-4 md:px-6" data-id="element-4927">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4928">
            <StatCard title="Materi Edukasi" value={seriesData.stats.konten.toString()} icon={FileText} description="Siap Unduh" data-id="element-4929" />
            <StatCard title="Total Event" value={seriesData.stats.event.toString()} icon={Calendar} description="Nasional" data-id="element-4930" />
            <StatCard title="Partisipan" value={seriesData.stats.partisipan} icon={Users} description="Terlibat" data-id="element-4931" />
            <StatCard title="Sampah Terkelola" value={seriesData.stats.tonase} icon={Recycle} description="Ton" data-id="element-4932" />
          </div>
        </section>

        {/* Content Tabs */}
        <section className="py-12 container mx-auto px-4 md:px-6" data-id="element-4933">
          <div className="flex border-b border-gray-200 mb-8 overflow-x-auto" data-id="element-4934">
            {(['tentang', 'konten', 'event', 'galeri'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4935">
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>)}
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]" data-id="element-4936">
            {activeTab === 'tentang' && <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-4937">
                <div className="md:col-span-2 space-y-6 text-gray-600 leading-relaxed" data-id="element-4938">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-4939">
                    Latar Belakang
                  </h2>
                  <p data-id="element-4940">
                    Hari Peduli Sampah Nasional (HPSN) yang diperingati setiap
                    tanggal 21 Februari menjadi momentum penting bagi
                    pengelolaan sampah di Indonesia. HPSN 2024 mengusung tema
                    "Atasi Sampah Plastik dengan Cara Produktif" untuk mendorong
                    ekonomi sirkular.
                  </p>
                  <p data-id="element-4941">
                    Melalui series ini, kami mengajak seluruh elemen masyarakat,
                    pemerintah daerah, dan pelaku usaha untuk berkolaborasi
                    dalam menciptakan ekosistem pengelolaan sampah yang
                    berkelanjutan dan bernilai ekonomi.
                  </p>

                  <h3 className="text-xl font-bold text-gray-900 mt-8" data-id="element-4942">
                    Tujuan Utama
                  </h3>
                  <ul className="space-y-3" data-id="element-4943">
                    {['Meningkatkan kesadaran publik tentang bahaya sampah plastik', 'Mendorong implementasi pemilahan sampah dari sumber', 'Memperkuat rantai nilai daur ulang sampah plastik', 'Mengurangi timbulan sampah ke TPA'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-4944">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" data-id="element-4945" />
                        <span data-id="element-4946">{item}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="space-y-6" data-id="element-4947">
                  <Card data-id="element-4948">
                    <CardContent className="p-6" data-id="element-4949">
                      <h3 className="font-bold text-gray-900 mb-4" data-id="element-4950">
                        Target Audience
                      </h3>
                      <div className="flex flex-wrap gap-2" data-id="element-4951">
                        {['Rumah Tangga', 'Sekolah', 'Komunitas', 'UMKM', 'Pemerintah Desa'].map(tag => <Badge key={tag} variant="secondary" data-id="element-4952">
                            {tag}
                          </Badge>)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-primary-50 border-primary-100" data-id="element-4953">
                    <CardContent className="p-6" data-id="element-4954">
                      <h3 className="font-bold text-primary-900 mb-2" data-id="element-4955">
                        Ingin Berkolaborasi?
                      </h3>
                      <p className="text-sm text-primary-700 mb-4" data-id="element-4956">
                        Daftarkan komunitas atau instansi Anda sebagai mitra
                        resmi HPSN 2024.
                      </p>
                      <Button className="w-full bg-primary-600 hover:bg-primary-700" data-id="element-4957">
                        Daftar Mitra
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>}

            {activeTab === 'konten' && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4958">
                {[1, 2, 3, 4, 5, 6].map(i => <Card key={i} className="group hover:shadow-lg transition-all" data-id="element-4959">
                    <div className="h-40 bg-gray-100 flex items-center justify-center relative overflow-hidden" data-id="element-4960">
                      {i % 2 === 0 ? <Video className="h-12 w-12 text-gray-400" data-id="element-4961" /> : <FileText className="h-12 w-12 text-gray-400" data-id="element-4962" />}
                      <div className="absolute top-2 right-2" data-id="element-4963">
                        <Badge className="bg-white/90 text-gray-700 hover:bg-white" data-id="element-4964">
                          {i % 2 === 0 ? 'Video' : 'Poster'}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4" data-id="element-4965">
                      <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600" data-id="element-4966">
                        Materi Edukasi HPSN 2024 - Bagian {i}
                      </h3>
                      <div className="flex justify-between items-center mt-4" data-id="element-4967">
                        <span className="text-xs text-gray-500" data-id="element-4968">2.4 MB</span>
                        <Button size="sm" variant="outline" className="h-8" data-id="element-4969">
                          <Download className="h-3 w-3 mr-1" data-id="element-4970" /> Unduh
                        </Button>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>}

            {activeTab === 'event' && <div className="space-y-4" data-id="element-4971">
                {[1, 2, 3].map(i => <Card key={i} className="hover:shadow-md transition-shadow" data-id="element-4972">
                    <div className="flex flex-col md:flex-row" data-id="element-4973">
                      <div className="w-full md:w-48 h-32 md:h-auto bg-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-4974">
                        <Calendar className="h-8 w-8 text-gray-400" data-id="element-4975" />
                      </div>
                      <div className="p-6 flex-1" data-id="element-4976">
                        <div className="flex justify-between items-start mb-2" data-id="element-4977">
                          <Badge variant="outline" data-id="element-4978">Clean Up</Badge>
                          <span className="text-sm text-gray-500" data-id="element-4979">
                            25 Feb 2024
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4980">
                          Aksi Bersih Pantai Nasional #{i}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2" data-id="element-4981">
                          Kegiatan bersih pantai serentak di 5 lokasi utama di
                          Indonesia sebagai puncak peringatan HPSN.
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-4982">
                          <div className="flex items-center gap-1" data-id="element-4983">
                            <MapPin className="h-4 w-4" data-id="element-4984" /> Jakarta, Bali, Manado
                          </div>
                          <div className="flex items-center gap-1" data-id="element-4985">
                            <Users className="h-4 w-4" data-id="element-4986" /> 500+ Peserta
                          </div>
                        </div>
                      </div>
                      <div className="p-6 flex items-center border-t md:border-t-0 md:border-l border-gray-100" data-id="element-4987">
                        <Link to={`/event/${i}`} className="w-full" data-id="element-4988">
                          <Button className="w-full" data-id="element-4989">Lihat Detail</Button>
                        </Link>
                      </div>
                    </div>
                  </Card>)}
              </div>}

            {activeTab === 'galeri' && <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4990">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-lg overflow-hidden relative group cursor-pointer" data-id="element-4991">
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100" data-id="element-4992">
                      <ImageIcon className="h-8 w-8 text-gray-400" data-id="element-4993" />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-4994">
                      <span className="text-white font-medium" data-id="element-4995">Lihat Foto</span>
                    </div>
                  </div>)}
              </div>}
          </div>
        </section>
      </main>

      <Footer data-id="element-4996" />
    </div>;
}